import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import SmartDataTable from "../../../components/DataTable/SmartDataTable";
import ModalShell from "../../../components/Modal/ModalShell";
import { BrandLogo } from "../../../components/logo";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { listQuestions, getQuestion } from "../../../services/questions.service";
import { listCategories } from "../../../services/categories.service";
import { getQuestionnaire, updateQuestionnaire } from "../../../services/questionnaires.service";
import { notifySuccess, notifyWarning } from "../../../services/notificationBus";
import { reportError } from "../../../services/errorBus";
import QuestionnairePreview from "./QuestionnairePreview";
import { TYPE_LABELS, appendQuestions, buildCategoryIndex, buildEditorPayload, contentSignature, moveQuestion, normalizeQuestion, parseDraft, toEditorContent } from "./editorModel";
import "./QuestionnaireEditor.css";

const EMPTY = [];
const EMPTY_CONTENT = { name: "", questions: [] };

export default function QuestionnaireEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nameInput = useRef(null);
  const searchInput = useRef(null);
  const storageWarningShown = useRef(false);
  const draftKey = "teamassess:questionnaire-draft:" + (me?.id || me?.email || "admin") + ":" + id;

  const bankQuery = useQuery({ queryKey: ["questions"], queryFn: listQuestions, staleTime: 30_000 });
  const categoriesQuery = useQuery({ queryKey: ["categories-with-subs"], queryFn: listCategories, staleTime: 60_000 });
  const questionnaireQuery = useQuery({
    queryKey: ["questionnaire", id], queryFn: () => getQuestionnaire(id), enabled: !!id, staleTime: 10_000,
  });
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [lastRemoved, setLastRemoved] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState("selected");
  const [categoryId, setCategoryId] = useState(null);
  const [subcategoryId, setSubcategoryId] = useState(null);
  const [type, setType] = useState(null);
  const [search, setSearch] = useState("");
  const [bankSelection, setBankSelection] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(8);

  const ready = editor?.id === id;
  const content = ready ? editor.content : EMPTY_CONTENT;
  const dirty = ready && contentSignature(content) !== editor.baseline;
  const disabled = !ready || busy || !!recovery;
  const nameError = nameTouched && !content.name.trim() ? "Escribe el nombre del cuestionario." : "";

  useEffect(() => {
    if (!questionnaireQuery.data || editor?.id === id) return;
    const initial = toEditorContent(questionnaireQuery.data);
    setEditor({ id, content: initial, baseline: contentSignature(initial) });
    setLastRemoved(null);
    setBankSelection([]);
    setNameTouched(false);
    try {
      const draft = parseDraft(sessionStorage.getItem(draftKey));
      setRecovery(draft && contentSignature(draft) !== contentSignature(initial) ? draft : null);
    } catch {
      setRecovery(null);
    }
  }, [id, questionnaireQuery.data, editor?.id, draftKey]);

  useEffect(() => {
    if (!ready || recovery) return;
    try {
      if (dirty) sessionStorage.setItem(draftKey, JSON.stringify({ version: 1, content }));
      else sessionStorage.removeItem(draftKey);
    } catch {
      if (dirty && !storageWarningShown.current) {
        storageWarningShown.current = true;
        notifyWarning("No se pudo respaldar el borrador en esta pestaña. Guarda los cambios antes de salir.");
      }
    }
  }, [ready, recovery, dirty, content, draftKey]);

  useEffect(() => {
    if (!dirty && !busy) return;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty, busy]);

  const categoryIndex = useMemo(() => buildCategoryIndex(categoriesQuery.data || EMPTY), [categoriesQuery.data]);
  const bank = useMemo(() => (bankQuery.data || EMPTY).map((question) => normalizeQuestion(question, categoryIndex)), [bankQuery.data, categoryIndex]);
  const bankById = useMemo(() => new Map(bank.map((question) => [question.id, question])), [bank]);
  const missingQuestions = content.questions.filter((question) => !bankById.has(question.id));
  const detailQueries = useQueries({
    queries: missingQuestions.map((question) => ({
      queryKey: ["editor-question", question.id], queryFn: () => getQuestion(question.id),
      enabled: bankQuery.isSuccess, staleTime: 60_000, retry: false,
    })),
  });
  const selectedQuestions = content.questions.map((question) => {
    const missingIndex = missingQuestions.findIndex((item) => item.id === question.id);
    const detail = missingIndex >= 0 ? detailQueries[missingIndex]?.data : null;
    const fallback = questionnaireQuery.data?.questions?.find((item) => item.id === question.id);
    return { ...(bankById.get(question.id) || normalizeQuestion(detail || fallback || question, categoryIndex)), ...question };
  });
  const chosenIds = new Set(content.questions.map((question) => question.id));
  const available = bank.filter((question) => !chosenIds.has(question.id));
  const filteredBank = available.filter((question) =>
    (!categoryId || question.categoryId === categoryId) &&
    (!subcategoryId || question.subcategoryId === subcategoryId) &&
    (!type || question.type === type) &&
    (!search.trim() || question.text.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))
  );
  const checkedQuestions = available.filter((question) => bankSelection.includes(question.id));
  const categoryOptions = [...categoryIndex.values()].filter((category) => !category.parentId).map((category) => ({ label: category.name, value: category.id }));
  const subcategoryOptions = [...categoryIndex.values()].filter((category) => category.parentId && (!categoryId || category.parentId === categoryId))
    .map((category) => ({ label: category.name, value: category.id }));
  const previewReady = selectedQuestions.length > 0 && selectedQuestions.every((question) => question.type);
  const bankLoading = bankQuery.isPending || categoriesQuery.isPending;
  const bankError = bankQuery.isError || categoriesQuery.isError;

  useEffect(() => { setFirst(0); }, [search, categoryId, subcategoryId, type]);
  useEffect(() => {
    if (first >= filteredBank.length && first > 0) setFirst(Math.max(0, Math.ceil(filteredBank.length / rows) - 1) * rows);
  }, [filteredBank.length, first, rows]);

  const changeContent = (update) => {
    if (disabled) return;
    setEditor((previous) => ({ ...previous, content: update(previous.content) }));
  };
  const add = (questions) => {
    changeContent((previous) => appendQuestions(previous, questions));
    setBankSelection((previous) => previous.filter((questionId) => !questions.some((question) => question.id === questionId)));
  };
  const remove = (question) => {
    setLastRemoved({ question: content.questions.find((item) => item.id === question.id), index: content.questions.findIndex((item) => item.id === question.id) });
    changeContent((previous) => ({ ...previous, questions: previous.questions.filter((item) => item.id !== question.id) }));
  };
  const undoRemove = () => {
    if (!lastRemoved) return;
    changeContent((previous) => {
      if (previous.questions.some((question) => question.id === lastRemoved.question.id)) return previous;
      const questions = [...previous.questions];
      questions.splice(Math.min(lastRemoved.index, questions.length), 0, lastRemoved.question);
      return { ...previous, questions };
    });
    setLastRemoved(null);
  };
  const clearDraft = () => { try { sessionStorage.removeItem(draftKey); } catch {} };

  const save = async (event) => {
    event?.preventDefault();
    if (disabled || !dirty) return;
    setNameTouched(true);
    if (!content.name.trim()) { nameInput.current?.focus(); return; }
    setBusy(true);
    try {
      const payload = buildEditorPayload(content, questionnaireQuery.data);
      const response = await updateQuestionnaire(id, payload);
      const saved = response && typeof response.name === "string" && Array.isArray(response.questions) ? response : { ...questionnaireQuery.data, ...payload };
      const savedContent = toEditorContent(saved);
      setEditor({ id, content: savedContent, baseline: contentSignature(savedContent) });
      setLastRemoved(null);
      queryClient.setQueryData(["questionnaire", id], saved);
      queryClient.invalidateQueries({ queryKey: ["questionnaires"] });
      clearDraft();
      notifySuccess("Cuestionario guardado.");
    } catch (error) {
      reportError(error, "No se pudo guardar el cuestionario. Tus cambios siguen en el editor; inténtalo de nuevo.");
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const questionnaire = await questionnaireQuery.refetch();
      const questions = await bankQuery.refetch();
      const categories = await categoriesQuery.refetch();
      const error = questionnaire.error || questions.error || categories.error;
      if (error) throw error;
      for (const detail of detailQueries) if (detail.isError) await detail.refetch();
      const initial = toEditorContent(questionnaire.data);
      setEditor({ id, content: initial, baseline: contentSignature(initial) });
      setLastRemoved(null);
      setBankSelection([]);
      setNameTouched(false);
      clearDraft();
    } catch (error) {
      reportError(error, "No se pudo actualizar el editor. Tus cambios siguen aquí; inténtalo de nuevo.");
    } finally {
      setBusy(false);
    }
  };
  const requestAction = (action) => {
    if (busy) return;
    if (dirty) setPendingAction(action);
    else if (action === "refresh") refresh();
    else navigate("/questionnaire/all");
  };
  const showBank = () => { setMobileTab("bank"); requestAnimationFrame(() => searchInput.current?.focus()); };
  const metadata = (question) => (
    <div className="q-question-meta">
      <span className={"q-type q-type--" + (question.type || "unknown")}>{TYPE_LABELS[question.type] || "Detalle no disponible"}</span>
      {question.categoryName && <span>{[question.categoryName, question.subcategoryName].filter(Boolean).join(" / ")}</span>}
    </div>
  );

  return (
    <main className="q-editor-page teamassess-form">
      <header className="q-editor-topbar">
        <div className="q-editor-brand">
          <Button type="button" icon="pi pi-arrow-left" text aria-label="Volver a cuestionarios" tooltip="Volver a cuestionarios"
            disabled={busy || !!recovery} onClick={() => requestAction("leave")} />
          <BrandLogo />
          <span className="q-editor-location">Cuestionarios</span>
        </div>
        <Button type="button" icon={theme === "dark" ? "pi pi-sun" : "pi pi-moon"} text
          aria-label="Cambiar tema" tooltip="Cambiar tema" onClick={toggleTheme} />
      </header>

      <form id="questionnaire-editor-form" className="q-editor-toolbar" onSubmit={save} noValidate>
        <div className="q-editor-title-field">
          <label htmlFor="questionnaire-name">Nombre del cuestionario</label>
          <InputText ref={nameInput} id="questionnaire-name" value={content.name} required disabled={disabled}
            onChange={(event) => changeContent((previous) => ({ ...previous, name: event.target.value }))}
            onBlur={() => setNameTouched(true)} placeholder="Nombre del cuestionario"
            aria-invalid={!!nameError} aria-describedby={nameError ? "questionnaire-name-error" : undefined} />
          {nameError && <small id="questionnaire-name-error" className="ta-field-error">{nameError}</small>}
        </div>
        <div className="q-editor-save-actions">
          <span className="q-editor-save-state" role="status">
            <i aria-hidden="true" className={"pi " + (busy ? "pi-spin pi-spinner" : dirty ? "pi-circle-fill" : "pi-check-circle")} />
            {busy ? "Procesando..." : !ready ? "Cargando..." : dirty ? "Cambios pendientes" : "Guardado"}
          </span>
          <Button type="button" icon="pi pi-refresh" text aria-label="Actualizar editor" tooltip="Actualizar editor"
            disabled={busy || !!recovery} onClick={() => requestAction("refresh")} />
          <Button type="button" icon="pi pi-eye" outlined label="Vista previa" disabled={disabled || !previewReady} onClick={() => setPreviewOpen(true)} />
          <Button type="submit" icon="pi pi-save" label="Guardar" loading={busy} disabled={disabled || !dirty} />
        </div>
      </form>

      {questionnaireQuery.isError && <div className="q-editor-load-error" role="alert">
        No se pudo cargar el cuestionario. <Button type="button" label="Reintentar" icon="pi pi-refresh" text onClick={refresh} disabled={busy} />
      </div>}

      <div className="q-editor-tabs" role="tablist" aria-label="Paneles del editor">
        <button type="button" role="tab" id="selected-tab" aria-controls="selected-panel" aria-selected={mobileTab === "selected"} onClick={() => setMobileTab("selected")}>
          Cuestionario <span>{content.questions.length}</span>
        </button>
        <button type="button" role="tab" id="bank-tab" aria-controls="bank-panel" aria-selected={mobileTab === "bank"} onClick={() => setMobileTab("bank")}>
          Banco de preguntas
        </button>
      </div>

      <div className={"q-editor-body q-editor-tab--" + mobileTab}>
        <section id="bank-panel" className="q-editor-bank" aria-labelledby="bank-title">
          <div className="q-panel-heading"><h2 id="bank-title">Banco de preguntas</h2><span>{filteredBank.length} disponibles</span></div>
          <div className="q-bank-filters">
            <div className="q-bank-search">
              <i className="pi pi-search" aria-hidden="true" />
              <InputText ref={searchInput} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar preguntas..."
                aria-label="Buscar preguntas" disabled={bankLoading} />
            </div>
            <Dropdown value={categoryId} inputId="bank-category" aria-label="Filtrar por categoría"
              onChange={(event) => { setCategoryId(event.value); setSubcategoryId(null); }}
              options={categoryOptions} placeholder="Categoría" showClear disabled={bankLoading || bankError} panelClassName="teamassess-form-menu" />
            <Dropdown value={subcategoryId} inputId="bank-subcategory" aria-label="Filtrar por subcategoría"
              onChange={(event) => setSubcategoryId(event.value)} options={subcategoryOptions}
              placeholder="Subcategoría" showClear disabled={bankLoading || bankError} panelClassName="teamassess-form-menu" />
            <Dropdown value={type} inputId="bank-type" aria-label="Filtrar por tipo de respuesta"
              onChange={(event) => setType(event.value)} options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))}
              placeholder="Tipo de respuesta" showClear panelClassName="teamassess-form-menu" />
            <Button type="button" icon="pi pi-filter-slash" text aria-label="Limpiar filtros" tooltip="Limpiar filtros"
              disabled={!search && !categoryId && !subcategoryId && !type}
              onClick={() => { setSearch(""); setCategoryId(null); setSubcategoryId(null); setType(null); }} />
          </div>
          {bankError && <p className="q-editor-load-error" role="alert">No se pudo actualizar el banco. Vuelve a intentarlo con Actualizar editor.</p>}
          {/* Cell actions depend on editor state, not only the question row data. */}
          <SmartDataTable className="q-bank-table" value={filteredBank} dataKey="id" cellMemo={false}
            selectionMode="multiple" selection={checkedQuestions}
            onSelectionChange={(selection) => setBankSelection(selection.map((question) => question.id))}
            isDataSelectable={() => !disabled} selectionPageOnly
            columns={[
              { field: "text", header: "Pregunta", sortable: true, body: (question) => <div className="q-bank-question"><p>{question.text}</p>{metadata(question)}</div> },
              { header: "", headerStyle: { width: "3.5rem" }, body: (question) =>
                <Button type="button" icon="pi pi-plus" text aria-label={"Agregar pregunta: " + question.text} tooltip="Agregar pregunta"
                  disabled={disabled} onClick={() => add([question])} /> },
            ]}
            paginator rows={rows} first={first} rowsPerPageOptions={[8, 16, 24]}
            onPage={(event) => { setFirst(event.first); setRows(event.rows); }}
            loading={bankLoading || !ready} tableStyle={{ width: "100%", tableLayout: "fixed" }}
            emptyMessage={bankError ? "El banco no está disponible" : "No hay preguntas que coincidan"} />
          <div className="q-bank-actions">
            <Button type="button" icon="pi pi-plus" label={"Agregar seleccionadas (" + checkedQuestions.length + ")"}
              disabled={disabled || !checkedQuestions.length} onClick={() => add(checkedQuestions)} />
            <Button type="button" icon="pi pi-angle-double-right" text label="Agregar todas las filtradas"
              disabled={disabled || bankLoading || bankError || !filteredBank.length} onClick={() => add(filteredBank)} />
          </div>
        </section>

        <section id="selected-panel" className="q-editor-selected" aria-labelledby="selected-title">
          <div className="q-panel-heading">
            <h2 id="selected-title">Cuestionario <span className="q-question-count">{content.questions.length}</span></h2>
            <Button type="button" icon="pi pi-undo" text aria-label="Deshacer última eliminación" tooltip="Deshacer última eliminación"
              disabled={disabled || !lastRemoved || chosenIds.has(lastRemoved?.question.id)} onClick={undoRemove} />
          </div>
          <SmartDataTable className="q-selected-table" value={selectedQuestions} dataKey="id" cellMemo={false}
            leadingColumns={[{ rowReorder: !disabled, headerStyle: { width: "2.5rem" } }]}
            reorderableRows={!disabled} onRowReorder={(event) => changeContent((previous) => ({
              ...previous, questions: event.value.map(({ id: questionId, required }) => ({ id: questionId, required })),
            }))}
            columns={[{ field: "text", header: "Preguntas en orden", body: (question) => {
              const index = content.questions.findIndex((item) => item.id === question.id);
              return <article className="q-selected-question">
                <div className="q-selected-title"><span className="q-question-number">{index + 1}</span><h3>{question.text || "Pregunta sin detalle"}</h3></div>
                {metadata(question)}
                <div className="q-selected-controls">
                  <div className="q-required-control">
                    <InputSwitch inputId={"required-" + question.id} checked={question.required} disabled={disabled}
                      onChange={(event) => changeContent((previous) => ({ ...previous, questions: previous.questions.map((item) => item.id === question.id ? { ...item, required: event.value } : item) }))} />
                    <label htmlFor={"required-" + question.id}>Obligatoria</label>
                  </div>
                  <div className="q-question-actions">
                    <Button type="button" icon="pi pi-arrow-up" text aria-label={"Subir pregunta " + (index + 1)} tooltip="Subir pregunta"
                      disabled={disabled || index === 0} onClick={() => changeContent((previous) => moveQuestion(previous, question.id, -1))} />
                    <Button type="button" icon="pi pi-arrow-down" text aria-label={"Bajar pregunta " + (index + 1)} tooltip="Bajar pregunta"
                      disabled={disabled || index === selectedQuestions.length - 1} onClick={() => changeContent((previous) => moveQuestion(previous, question.id, 1))} />
                    <Button type="button" icon="pi pi-trash" text severity="danger" aria-label={"Quitar pregunta " + (index + 1)} tooltip="Quitar pregunta"
                      disabled={disabled} onClick={() => remove(question)} />
                  </div>
                </div>
              </article>;
            } }]}
            loading={!ready} tableStyle={{ width: "100%", tableLayout: "fixed" }}
            emptyMessage={<div className="q-selected-empty"><i className="pi pi-list" aria-hidden="true" /><p>Sin preguntas</p>
              <Button type="button" label="Agregar preguntas" icon="pi pi-plus" outlined onClick={showBank} disabled={disabled} /></div>} />
        </section>
      </div>

      {previewOpen && <QuestionnairePreview name={content.name} questions={selectedQuestions} onHide={() => setPreviewOpen(false)} />}
      <ModalShell visible={!!pendingAction} title="¿Descartar cambios?" onHide={() => setPendingAction(null)} width="30rem"
        footer={<div>
          <Button type="button" label="Seguir editando" text autoFocus onClick={() => setPendingAction(null)} />
          <Button type="button" label={pendingAction === "refresh" ? "Descartar y actualizar" : "Salir sin guardar"} severity="danger"
            onClick={() => {
              const action = pendingAction;
              setPendingAction(null);
              if (action === "refresh") refresh();
              else { clearDraft(); navigate("/questionnaire/all"); }
            }} />
        </div>}>
        <p>Hay cambios sin guardar en este cuestionario.</p>
      </ModalShell>
      <Dialog visible={!!recovery} header="Recuperar borrador" className="teamassess-dialog" style={{ width: "30rem" }}
        modal closable={false} closeOnEscape={false} draggable={false} onHide={() => {}}
        footer={<div>
          <Button type="button" label="Descartar borrador" text onClick={() => { clearDraft(); setRecovery(null); }} />
          <Button type="button" label="Recuperar" icon="pi pi-history" autoFocus onClick={() => {
            setEditor((previous) => ({ ...previous, content: recovery }));
            setRecovery(null);
          }} />
        </div>}>
        <p>Esta pestaña tiene cambios sin guardar para este cuestionario.</p>
      </Dialog>
    </main>
  );
}
